import React, { useState } from 'react';
import { ActivityIndicator, StyleProp, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';
import MyAppText from './MyAppText';

export type ButtonTypes = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'destructive_secondary';

interface Props {
    icon?: IconDefinition;
    text?: string;
    onPress: () => (Promise<unknown> | unknown);
    /**
     * button style
     */
    style?: StyleProp<ViewStyle>;
    /**
     * text style
     */
    textStyle?: TextStyle;
    /**
     * button style
     */
    contentContainerStyle?: StyleProp<ViewStyle>;
    isLoading?: boolean;
    disabled?: boolean;
    isActive?: boolean;
    type?: ButtonTypes;
}

function CustomButton({ icon, text, onPress, style, textStyle, isLoading = false, disabled = false, type = 'primary', contentContainerStyle, isActive = false }: Props) {
    const { styles } = useStyles(stylesheet);

    const [internalIsLoading, setInternalIsLoading] = useState(false);


    function buttonContent(hovered: boolean, disabled: boolean) {
        if (isLoading || internalIsLoading) {
            return <ActivityIndicator size='small' color={styles.loadingColor.color} />;
        }
        return (
            <React.Fragment>
                {icon && <FontAwesomeIcon style={styles.buttonIcon(type, hovered, disabled, textStyle)} fontSize={textStyle?.fontSize || 16} icon={icon} />}
                {text && <MyAppText style={[styles.buttonText(type, hovered, disabled), textStyle]}>{text}</MyAppText>}
            </React.Fragment>
        );
    }

    return (
        <TouchableOpacity
            style={contentContainerStyle}
            onPress={async () => {
                setInternalIsLoading(true);
                const res = onPress();
                await res;
                setInternalIsLoading(false);
            }}
            disabled={disabled || isLoading || internalIsLoading}
        >
            <Hoverable>
                {({ hovered }) => (
                    <View style={[styles.buttonContainer(type, hovered, disabled || isLoading || internalIsLoading, isActive), style]}>
                        {buttonContent(hovered, disabled)}
                    </View>
                )}
            </Hoverable>
        </TouchableOpacity>
    );
}

const stylesheet = createStyleSheet(theme => ({
    buttonContainer: (type: ButtonTypes, hovered: boolean, disabled: boolean, isActive: boolean) => {

        let backgroundColor: string | undefined;
        let borderColor: string | undefined;
        let borderWidth = 0;
        let paddingHorizontal = 20;

        if (type == 'primary') {
            backgroundColor = disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'secondary') {
            backgroundColor = isActive ? theme.colors.secondaryButton.activeBackgroundColor : disabled ? theme.colors.secondaryButton.disabledBackground : hovered ? theme.colors.secondaryButton.hoverBackground : theme.colors.secondaryButton.background;
            borderWidth = 1;
            borderColor = disabled ? theme.colors.secondaryButton.disabledBorderColor : hovered ? theme.colors.secondaryButton.hoverBorderColor : theme.colors.secondaryButton.borderColor;
        } else if (type == 'tertiary') {
            backgroundColor = 'transparent';
            borderWidth = 0;
            borderColor = undefined;
            paddingHorizontal = 0;
        } else if (type == 'destructive') {
            backgroundColor = disabled ? theme.colors.destructiveButton.disabledBackground : hovered ? theme.colors.destructiveButton.hoverBackground : theme.colors.destructiveButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'destructive_secondary') {
            backgroundColor = isActive ? theme.colors.destructiveSecondaryButton.activeBackgroundColor : disabled ? theme.colors.destructiveSecondaryButton.disabledBackground : hovered ? theme.colors.destructiveSecondaryButton.background : theme.colors.destructiveSecondaryButton.background;
            borderWidth = 1;
            borderColor = hovered && !disabled ? theme.colors.destructiveSecondaryButton.hoverBorderColor : 'transparent';
        }

        return {
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: 5,
            borderWidth: borderWidth,
            borderColor: borderColor,
            paddingHorizontal: paddingHorizontal,
            width: '100%',
            columnGap: 10,
            height: 40,
            backgroundColor: backgroundColor,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'center',
            gap: 10
        };
    },

    buttonText: (type: ButtonTypes, hovered: boolean, disabled: boolean) => {

        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background;
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        } else if (type == 'destructive_secondary') {
            color = disabled ? theme.colors.destructiveSecondaryButton.disabledText : hovered ? theme.colors.destructiveSecondaryButton.hoverText : theme.colors.destructiveSecondaryButton.text;
        }

        return {
            fontWeight: '600',
            color: color,
            fontSize: 16,
        };
    },

    buttonIcon: (type: ButtonTypes, hovered: boolean, disabled: boolean, textStyle: TextStyle | undefined) => {
        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background;
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        } else if (type == 'destructive_secondary') {
            color = disabled ? theme.colors.destructiveSecondaryButton.disabledText : hovered ? theme.colors.destructiveSecondaryButton.hoverText : theme.colors.destructiveSecondaryButton.text;
        }

        if (textStyle?.color) {
            color = String(textStyle?.color);
        }

        return {
            color,
        };
    },

    loadingColor: {
        color: theme.colors.loadingColor
    }
}));

export default CustomButton;
